import React from 'react';
import { useAppTheme } from '@/context/ThemeContext';

type FAQItem = {
  question: string;
  answer: string;
};

const FAQ: React.FC = () => {
  const { themeConfig } = useAppTheme();

  const faqItems: FAQItem[] = [
    {
      question: 'What is Catch of the Day?',
      answer: 'Catch of the Day is a platform that connects food businesses with surplus food to people and charities, reducing food waste and helping those in need. Our platform allows businesses to list available food items, manage donations, and track social impact.'
    },
    {
      question: 'How do I add products to my inventory?',
      answer: 'You can add products to your inventory by navigating to the Products section in the sidebar, then clicking on "Add Product". Fill out the product details form including name, description, price, quantity, and images. You can also use the bulk upload feature to add multiple products at once.'
    },
    {
      question: 'How do I manage donation requests?',
      answer: 'Donation requests can be managed in the Orders section under "Donation Requests". Here you can view all incoming requests, approve or decline them, and set pickup times. You can also set automatic approval rules in your Business Settings.'
    },
    {
      question: 'How do I view my business statistics?',
      answer: 'Your business statistics are available in the Statistics section. You can view ratings and reviews from customers, track your social impact metrics, and analyze your business performance through various charts and reports.'
    },
    {
      question: 'How do I add staff members to my account?',
      answer: 'You can add staff members by going to Settings > Staff Management. Click on "Add Staff Member", enter their email address and select their role (Manager, Supervisor, or Staff). They will receive an invitation email to join your business account.'
    },
    {
      question: 'What happens if I can\'t fulfill an order?',
      answer: 'If you cannot fulfill an order, you should cancel it as soon as possible. Navigate to the Orders section, find the order, and click "Cancel Order". You will be asked to provide a reason for cancellation, which will be shared with the customer.'
    },
    {
      question: 'How do I update my business information?',
      answer: 'You can update your business information by going to Settings > Business Settings. Here you can edit your business name, address, contact information, operating hours, and upload a new logo or cover image.'
    },
    {
      question: 'How do I track the social impact of my donations?',
      answer: 'Your social impact is tracked automatically in the Statistics > Social Impact section. Here you can see metrics such as total food waste prevented, CO2 emissions saved, and number of meals provided to those in need through your donations.'
    },
    {
      question: 'How do customer reviews work?',
      answer: 'Customers can leave reviews after completing an order from your business. Reviews include a star rating and optional feedback on specific aspects like food quality and service. You can view and respond to reviews in the Statistics > Ratings & Reviews section.'
    },
    {
      question: 'How do I contact support if I have a problem?',
      answer: 'You can contact our support team through the Contact Support page in the Help Center. Alternatively, you can email us directly at support@catchoftheday.com or use the live chat feature during business hours.'
    }
  ];

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold" style={{ color: themeConfig.primary }}>
          Frequently Asked Questions
        </h1>
        <p className="text-gray-600 mt-2">
          Find answers to common questions about using the Catch of the Day business portal.
        </p>
      </div>

      <div className="space-y-4">
        {faqItems.map((item, index) => (
          <div 
            key={index}
            className="border rounded-lg overflow-hidden mb-4"
            style={{ borderColor: themeConfig.accent }}
          >
            <div
              className="w-full px-6 py-4 text-left"
              style={{ backgroundColor: themeConfig.cardBackground }}
            >
              <h3 className="font-medium text-lg mb-2" style={{ color: themeConfig.text }}>
                {item.question}
              </h3>
              <div className="px-0 py-2">
                <p className="text-gray-600">
                  {item.answer}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default FAQ;
